import { useState } from "react";
import axios from "axios";
import pagarme from "pagarme/browser";
import { swalCustom } from "../../utils/functions";
import notify from "../UI/toast";
import { cardsApi } from "../../page/OrderResumePag/_services/api";
import { getCardBinAndBrand } from "../../page/OrderResumePag/_utils/card";
import {
  addAddress,
  getAddresses,
} from "../AddressComponent/services/addressService";
import { useUserData } from "../../hooks/UserContext";
import { useScreenTracking } from "../../hooks/useScreenTracking";
import { trackClick, trackCallbackMarketplace } from "../../utils/gtm";
import { tagIdFromLegacySuffix } from "../../utils/marketplaceTagIds";

const CARD_VALIDATION_MESSAGES = [
  ["card_holder_name", "Verifique o nome impresso no cartão."],
  ["card_number", "Verifique o número do cartão."],
  ["card_expiration_date", "Verifique a data de expiração do cartão."],
  ["card_cvv", "Verifique o código de segurança (CVV) do cartão."],
];

export function useCardManagement({
  cards,
  setCards,
  setRefreshCards,
  addresses,
}) {
  const [boxNewCard, setBoxNewCard] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [needsAddress, setNeedsAddress] = useState(false);
  const [isSavingAddress, setIsSavingAddress] = useState(false);
  const { user, setMainCard, setUpdateAddresses, addressesData, changeAddress } = useUserData();
  const screenName = useScreenTracking();

  const hasAddress = Boolean(addresses?.length || addressesData?.length);

  function track(title, contentType, suffix) {
    trackClick({
      title,
      contentType,
      screenName,
      tagId: tagIdFromLegacySuffix(suffix),
    });
  }

  function closeAddCardModal() {
    track("fechar-modal", "cadastrar-novo-cartao", "08");
    setBoxNewCard(false);
    setNeedsAddress(false);
  }

  function openAddCardModal() {
    track("adicionar", "cartoes-cadastrados", "06");
    setNeedsAddress(!hasAddress);
    setBoxNewCard(true);
  }

  async function saveNewAddress(payload) {
    setIsSavingAddress(true);
    try {
      const response = await addAddress(payload);
      const createdUuid = response?.data?.data?.uuid;

      try {
        const updatedList = await getAddresses();
        const created =
          (createdUuid &&
            updatedList.find((addr) => addr.uuid === createdUuid)) ||
          updatedList.find((addr) => addr.main) ||
          updatedList[updatedList.length - 1];

        if (created && changeAddress) {
          changeAddress(created);
        }
      } catch (selectError) {
        console.error("Erro ao selecionar endereço cadastrado:", selectError);
      }

      if (setUpdateAddresses) setUpdateAddresses(new Date());
      notify("Endereço cadastrado com sucesso.", "success");
      setNeedsAddress(false);
      return true;
    } catch (error) {
      console.error("Erro ao cadastrar endereço:", error);
      notify("Erro ao cadastrar endereço.", "error");
      throw error;
    } finally {
      setIsSavingAddress(false);
    }
  }

  function onClickDelCard(uuidCard) {
    track("excluir-cartao", "cartoes-cadastrados", "06");

    if (cards.length === 1) {
      notify(
        "Este cartão não pode ser excluído enquanto não houver outro cartão cadastrado.",
        "error",
      );
      return;
    }

    swalCustom
      .fire({
        html: '<p class="fw-bold fs-5 mb-0">Deseja remover essa forma de pagamento?</p>',
        showCancelButton: true,
        confirmButtonText: "REMOVER",
        cancelButtonText: "CANCELAR",
        icon: "question",
      })
      .then(async (result) => {
        if (result.isConfirmed) {
          try {
            const apiResult = await cardsApi.delete(
              `/v1/customer/card/${uuidCard}`,
            );
            trackCallbackMarketplace({
              contentType: "excluir-cartao",
              screenName: `marketplace-${screenName}`,
              status: !apiResult.status ? "erro" : "sucesso",
            });

            if (!apiResult.status) {
              notify(apiResult.message, "error");
              return;
            }
            notify("Cartão removido com sucesso", "success");
            if (setRefreshCards) setRefreshCards(new Date());
          } catch (err) {
            notify("Erro ao deletar cartão.", "error");
          }
        } else if (result.dismiss) {
          track("cancelar-exclusao-cartao", "cadastrar-novo-cartao", "08");
        }
      });
  }

  async function saveNewCard(values) {
    setIsLoading(true);

    const cardPagarme = {
      card_number: values.card_number,
      card_holder_name: values.card_holder_name,
      card_expiration_date: values.card_expiration_date?.replace("/", ""),
      card_cvv: values.card_cvv,
    };
    const { card: validation } = pagarme.validate({ card: cardPagarme });

    for (const [field, message] of CARD_VALIDATION_MESSAGES) {
      if (!validation[field]) {
        setIsLoading(false);
        notify(message, "error");
        return false;
      }
    }

    const [expirationMonth, expirationYear] =
      values.card_expiration_date.split("/");
    const cardNumber = values.card_number.replaceAll(" ", "");

    let cardHash;
    try {
      const { data: tokenData, status } = await axios.request({
        method: "POST",
        url: `${process.env.REACT_APP_PAGARME_API}?appId=${process.env.REACT_APP_PAGARME_APPID}`,
        headers: {
          accept: "application/json",
          "content-type": "application/json",
        },
        data: {
          type: "card",
          card: {
            number: cardNumber,
            holder_name: values.card_holder_name,
            exp_month: expirationMonth,
            exp_year: expirationYear,
            cvv: values.card_cvv,
          },
        },
      });
      trackCallbackMarketplace({
        status: status === 200 ? "sucesso" : "erro",
        contentType: "cadastrar-novo-cartao",
        screenName: `marketplace-${screenName}`,
      });
      if (status !== 200) {
        setIsLoading(false);
        notify("Erro ao cadastrar dados do cartão.", "error");
        return;
      }
      cardHash = tokenData?.id;
    } catch (err) {
      console.error(err);
      setIsLoading(false);
      notify("Erro ao cadastrar dados do cartão.", "error");
      return;
    }

    const { bin, brand } = getCardBinAndBrand(values.card_number);
    const lastFourDigits = values.card_number?.slice(-4);
    const createdCard = {
      brand,
      bin,
      expiration_month: expirationMonth,
      expiration_year: expirationYear,
      holder_cpf: values.card_document,
      holder_name: values.card_holder_name,
      last_four_digits: lastFourDigits,
      main: cards.length === 0,
      hash: cardHash,
    };

    try {
      const { data, status } = await cardsApi.post(
        `/v1/customer/card`,
        {
          cardToken: cardHash,
          bin,
          brand,
          expirationMonth,
          expirationYear,
          holderName: createdCard.holder_name,
          lastFourDigits,
          main: createdCard.main,
        },
      );
      if (status !== 200) {
        notify("Erro ao cadastrar o cartão", "error");
        return;
      }
      notify("Cartão cadastrado com sucesso.", "success");
      createdCard.uuid = data?.data?.uuid;
      if (setCards) setCards([createdCard, ...cards]);
      setMainCard(createdCard);
      if (setRefreshCards) setRefreshCards(new Date());
      setBoxNewCard(false);
    } catch (error) {
      notify("Erro ao cadastrar o cartão", "error");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }

  return {
    boxNewCard,
    openAddCardModal,
    closeAddCardModal,
    saveNewCard,
    saveNewAddress,
    onClickDelCard,
    isLoading,
    needsAddress,
    isSavingAddress,
  };
}
